import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { invalidateRegistryLists, KIND_TO_PROTO } from '../../../components/SharedRecord/registryResolveClient';
import { registryKeys } from '../queryKeys';
import { registryRecordClient } from '../records/registryRecordClient';
/** Query-key roots the three Registries tabs read their lists and profiles from. */ const TAB_KEYS = [
    'governmentOrg',
    'vehicle-identity',
    'ownCompanyProfile'
];
/** MergeRegistryEntities; on success every list, profile and merge-event read refetches. */ export function useMergeRegistryEntities(kind) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ loserId, survivorId, reason })=>{
            const resp = await registryRecordClient.mergeRegistryEntities({
                kind: KIND_TO_PROTO[kind],
                loserId,
                survivorId,
                reason
            });
            // The server answers with the survivor it resolved (a chain may end further on).
            return resp.survivor?.id || survivorId;
        },
        onSuccess: async ()=>{
            invalidateRegistryLists(qc);
            for (const key of TAB_KEYS)qc.invalidateQueries({
                queryKey: [
                    key
                ]
            });
            await qc.invalidateQueries({
                queryKey: registryKeys.all
            });
        }
    });
}
/** Points the open record (?record=) at `id`, remembering which record was merged away. */ export function useGoToMergedSurvivor() {
    const [, setParams] = useSearchParams();
    return useCallback((survivorId, loserId)=>setParams((prev)=>{
            const next = new URLSearchParams(prev);
            next.set('record', survivorId);
            next.set('mergedFrom', loserId);
            return next;
        }, {
            replace: true
        }), [
        setParams
    ]);
}
/**
 * A record URL whose row is gone from the lists may be a merged-away loser.
 * While `missing`, look up its unreversed merge event and replace the URL with
 * the survivor's. Returns `resolving` so the caller holds its "unavailable"
 * state until the lookup settles.
 */ export function useMergedRecordRedirect(kind, recordId, missing) {
    const goTo = useGoToMergedSurvivor();
    const enabled = missing && !!recordId;
    const events = useQuery({
        queryKey: registryKeys.mergeEvents(kind, recordId ?? ''),
        enabled,
        retry: false,
        queryFn: async ()=>(await registryRecordClient.listRegistryMergeEvents({
                kind: KIND_TO_PROTO[kind],
                registryId: recordId ?? ''
            })).events
    });
    const survivorId = events.data?.find((e)=>!e.reversedAt && e.loser?.id === recordId)?.survivor?.id;
    useEffect(()=>{
        if (enabled && recordId && survivorId && survivorId !== recordId) goTo(survivorId, recordId);
    }, [
        enabled,
        recordId,
        survivorId,
        goTo
    ]);
    return {
        resolving: enabled && (events.isLoading || !!survivorId)
    };
}
